import { Fragment, useCallback, useState } from 'react'
import { Container, InnerContainer, SliderButton } from './Tabs.styles'
import { TabsProps } from './Tabs.types'

import { Icon } from '@yes.technology/react-toolkit'
import { useTranslation } from 'react-i18n-lite'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import TabItemComponent from './TabItem'
import { VerticalSeparator } from './TabItem.styles'
import { tabsComponentSizes } from './Tabs.enum'
import usePaginatedItems from './hooks/usePaginatedItems'

export default function Tabs({
  heightClass = 'regular',
  textColor = '#303032',
  highlightColor,
  items,
  selectedItem,
  onItemSelect,
  disabled,
  focusable,
  showSeparators = true,
  contentContainerStyleMap,
  ...divProps
}: TabsProps) {
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(
    null
  )

  const { t } = useTranslation()
  const contentContainerStyles = useStyleMaps(contentContainerStyleMap)

  const containerRef = useCallback((node: HTMLElement | null) => {
    setContainerElement(node)
  }, [])

  const {
    currentPageItems,
    goToNextPage,
    goToPreviousPage,
    displayArrows,
    isNextArrowDisabled,
    isPrevArrowDisabled
  } = usePaginatedItems({
    items,
    fontSize: tabsComponentSizes[heightClass].fontSize,
    itemSpacing: tabsComponentSizes[heightClass].spaceBetween,
    containerElement,
    selectedItem,
    onItemSelect
  })

  const previousLabel = `${selectedItem?.des} - ${t('navigation-slider.previous')}`
  const nextLabel = `${selectedItem?.des} - ${t('navigation-slider.next')}`

  return (
    <Container heightClass={heightClass} role='tablist' {...divProps}>
      {displayArrows && (
        <SliderButton
          heightClass={heightClass}
          onClick={goToPreviousPage}
          type='button'
          aria-label={previousLabel}
          disabled={isPrevArrowDisabled}
          color={textColor}
        >
          <Icon iconName='NavigationLeft' icSize='auto' />
        </SliderButton>
      )}
      <InnerContainer
        ref={containerRef}
        style={contentContainerStyles}
        heightClass={heightClass}
      >
        {currentPageItems.map((tab, tabIndex) => (
          <Fragment key={tab?.uuid}>
            {showSeparators && tabIndex > 0 && (
              <VerticalSeparator heightClass={heightClass} $color={textColor} />
            )}
            <TabItemComponent
              tab={tab}
              selectedTabUuid={selectedItem?.uuid}
              highlightColor={highlightColor}
              heightClass={heightClass}
              textColor={textColor}
              onItemSelected={onItemSelect}
              disabled={disabled}
              focusable={focusable}
            />
          </Fragment>
        ))}
      </InnerContainer>

      {displayArrows && (
        <SliderButton
          onClick={goToNextPage}
          type='button'
          aria-label={nextLabel}
          heightClass={heightClass}
          disabled={isNextArrowDisabled}
          focusable={focusable}
          color={textColor}
        >
          <Icon iconName='NavigationRight' icSize='auto' />
        </SliderButton>
      )}
    </Container>
  )
}
